/* eslint-disable @typescript-eslint/naming-convention */
import { Resume } from 'packages/types'
import { SettingsFieldChangeHandler } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { TooltipV2 } from 'builder/components/Tooltip'
import { useTemplateCustomization } from '../../hooks/useTemplateCustomization'
import { SelectValue } from '../../../../../Select/types'
import { SelectContainer, SelectLabel, CustomSelect } from '../../styles'
import { FontContainer } from './styles'

const FALLBACK_FONT_WEIGHTS = {
  primaryFontWeight: 'regular',
  secondaryFontWeight: 'regular',
  bodyFontWeight: 'regular',
  sectionTitlesFontWeight: 'regular',
}

type Props = {
  resume: Resume
  handleSettingsFieldChange?: SettingsFieldChangeHandler
}

type FontWeightFieldIds =
  | 'primaryFontWeight'
  | 'secondaryFontWeight'
  | 'bodyFontWeight'
  | 'sectionTitlesFontWeight'

const TRANSLATION = 'builder.resume_editor.document_customization.sidebar.text_tab'

export function FontWeightsCustomization(props: Props) {
  const { i18n } = useI18n()

  const { currentTemplate, resumeSpecificTemplateSettings } = useTemplateCustomization(props.resume)
  const fontWeights = currentTemplate?.settings?.fontWeights || []

  const getFontWeightField = (field: FontWeightFieldIds) => {
    const defaultValue = currentTemplate?.settings?.[field]
    const currentValue = resumeSpecificTemplateSettings?.[field]
    const fallbackValue = FALLBACK_FONT_WEIGHTS[field]

    return currentValue || defaultValue || fallbackValue
  }

  const onSelect = (field: FontWeightFieldIds, newValue: SelectValue) => {
    if (typeof newValue !== 'string') return
    const currentValue = getFontWeightField(field)
    props.handleSettingsFieldChange?.(field, newValue, currentValue)
  }

  const primaryFWValue = getFontWeightField('primaryFontWeight')
  const secondaryFWValue = getFontWeightField('secondaryFontWeight')
  const bodyFWValue = getFontWeightField('bodyFontWeight')
  const sectionTitlesFWValue = getFontWeightField('sectionTitlesFontWeight')

  return (
    <FontContainer>
      <TooltipV2
        width={210}
        position="bottom"
        value={i18n.t(`${TRANSLATION}.primary_heading_label`)}
      >
        <SelectContainer>
          <SelectLabel>{i18n.t(`${TRANSLATION}.primary_heading_label`)}</SelectLabel>
          <CustomSelect
            onSelect={value => onSelect('primaryFontWeight', value)}
            options={fontWeights}
            useNativeSelectOnMobile={false}
            optionsListClassname="dark"
            selected={primaryFWValue}
          />
        </SelectContainer>
      </TooltipV2>

      <TooltipV2
        width={210}
        position="bottom"
        value={i18n.t(`${TRANSLATION}.secondary_heading_label`)}
      >
        <SelectContainer>
          <SelectLabel>{i18n.t(`${TRANSLATION}.secondary_heading_label`)}</SelectLabel>
          <CustomSelect
            onSelect={value => onSelect('secondaryFontWeight', value)}
            options={fontWeights}
            useNativeSelectOnMobile={false}
            optionsListClassname="dark"
            selected={secondaryFWValue}
          />
        </SelectContainer>
      </TooltipV2>

      <TooltipV2 value={i18n.t(`${TRANSLATION}.body_label`)} width={210} position="bottom">
        <SelectContainer>
          <SelectLabel>{i18n.t(`${TRANSLATION}.body_label`)}</SelectLabel>
          <CustomSelect
            onSelect={value => onSelect('bodyFontWeight', value)}
            options={fontWeights}
            useNativeSelectOnMobile={false}
            optionsListClassname="dark"
            selected={bodyFWValue}
          />
        </SelectContainer>
      </TooltipV2>

      <TooltipV2
        width={210}
        position="bottom"
        value={i18n.t(`${TRANSLATION}.section_titles_label`)}
      >
        <SelectContainer>
          <SelectLabel>{i18n.t(`${TRANSLATION}.section_titles_label`)}</SelectLabel>
          <CustomSelect
            onSelect={value => onSelect('sectionTitlesFontWeight', value)}
            options={fontWeights}
            useNativeSelectOnMobile={false}
            optionsListClassname="dark"
            selected={sectionTitlesFWValue}
          />
        </SelectContainer>
      </TooltipV2>
    </FontContainer>
  )
}
